import { Config } from "../../../Config";
import { FaceTecSDK } from "../../../core-sdk/FaceTecSDK.js/FaceTecSDK";
import { SampleAppUIFunctions } from "./SampleAppUIFunctions";
import { SoundFileUtilities } from "./SoundFileUtilities";
import { DeveloperStatusMessages } from "./DeveloperStatusMessages";
import ocrLocalizationJSON = require("../../../sample-app-resources/FaceTec_OCR_Customization.json");
import { SampleAppController } from "../SampleAppController";

enum VocalGuidanceMode {
  MINIMAL,
  FULL,
  OFF
}

export class SampleAppUtilities  {
  private static vocalGuidanceSoundFilesDirectory: string = "../sample-app-resources/Vocal_Guidance_Audio_Files/";
  private static vocalGuidanceOnPlayer: HTMLAudioElement = new Audio(this.vocalGuidanceSoundFilesDirectory + "vocal_guidance_on.mp3");
  private static vocalGuidanceOffPlayer: HTMLAudioElement = new Audio(this.vocalGuidanceSoundFilesDirectory + "vocal_guidance_off.mp3");
  private static vocalGuidanceMode: VocalGuidanceMode = VocalGuidanceMode.MINIMAL;

  public static setupAndFadeInMainUIOnInitializationSuccess(): void {
    SampleAppUtilities.setupVocalGuidancePlayers();
    SampleAppUtilities.fadeInMainUIContainer();
    SampleAppUtilities.enableControlButtons();

    if (SampleAppUtilities.isLikelyMobileDevice()) {
      SampleAppUtilities.fadeInVocalIconContainer();
    }
  }

  public static setupVocalGuidancePlayers(): void {
    SampleAppUtilities.vocalGuidanceOnPlayer.volume = 0.4;
    SampleAppUtilities.vocalGuidanceOffPlayer.volume = 0.4;

    SampleAppUtilities.vocalGuidanceOffPlayer.onended = function(): void {
      SampleAppUtilities.enableVocalGuidanceButtons();
    };

    SampleAppUtilities.vocalGuidanceOnPlayer.onended = function(): void {
      SampleAppUtilities.enableVocalGuidanceButtons();
    };
  }

  public static setVocalGuidanceMode(): void {
    this.disableVocalGuidanceButtons();

    if (!this.vocalGuidanceOnPlayer.paused || !this.vocalGuidanceOffPlayer.paused) {
      return;
    }

    let playPromise: Promise<void>;

    switch (SampleAppUtilities.vocalGuidanceMode) {
      case VocalGuidanceMode.OFF:
        SampleAppUtilities.vocalGuidanceMode = VocalGuidanceMode.MINIMAL;

        (document.getElementById("vocal-guidance-icon-minimal") as HTMLElement).style.display = "block";
        (document.getElementById("vocal-guidance-icon-full") as HTMLElement).style.display = "none";
        (document.getElementById("vocal-guidance-icon-off") as HTMLElement).style.display = "none";

        playPromise = SampleAppUtilities.vocalGuidanceOnPlayer.play();

        if (typeof playPromise !== "undefined") {
          playPromise.catch((_event: void): void => {
            // Play failed
          });
        }

        Config.currentCustomization.vocalGuidanceCustomization.mode = VocalGuidanceMode.MINIMAL;
        break;

      case VocalGuidanceMode.MINIMAL:
        SampleAppUtilities.vocalGuidanceMode = VocalGuidanceMode.FULL;

        (document.getElementById("vocal-guidance-icon-minimal") as HTMLElement).style.display = "none";
        (document.getElementById("vocal-guidance-icon-full") as HTMLElement).style.display = "block";
        (document.getElementById("vocal-guidance-icon-off") as HTMLElement).style.display = "none";

        playPromise = SampleAppUtilities.vocalGuidanceOnPlayer.play();

        if (typeof playPromise !== "undefined") {
          playPromise.catch((_event: void): void => {
            // Play failed
          });
        }

        Config.currentCustomization.vocalGuidanceCustomization.mode = VocalGuidanceMode.FULL;
        break;

      case VocalGuidanceMode.FULL:
        SampleAppUtilities.vocalGuidanceMode = VocalGuidanceMode.OFF;

        (document.getElementById("vocal-guidance-icon-minimal") as HTMLElement).style.display = "none";
        (document.getElementById("vocal-guidance-icon-full") as HTMLElement).style.display = "none";
        (document.getElementById("vocal-guidance-icon-off") as HTMLElement).style.display = "block";

        playPromise = SampleAppUtilities.vocalGuidanceOffPlayer.play();

        if (typeof playPromise !== "undefined") {
          playPromise.catch((_event: void): void => {
            // Play failed
          });
        }

        Config.currentCustomization.vocalGuidanceCustomization.mode = VocalGuidanceMode.OFF;
        break;
    }

    FaceTecSDK.setCustomization(Config.currentCustomization);
  }

  public static setVocalGuidanceSoundFiles(): void {
    const soundFileUtilities: SoundFileUtilities = new SoundFileUtilities();
    Config.currentCustomization = soundFileUtilities.setVocalGuidanceSoundFiles(Config.currentCustomization);
    FaceTecSDK.setCustomization(Config.currentCustomization);
  }

  public static setOCRLocalization(): void {
    // Set the strings to be used for group names, field names, and placeholder texts for the FaceTec ID Scan User OCR Confirmation Screen.
    // DEVELOPER NOTE: For this demo, we are using the template json file, 'FaceTec_OCR_Customization.json,' as the parameter in calling the configureOCRLocalization API.
    // For the configureOCRLocalization API parameter, you may use any object that follows the same structure and key naming as the template json file, 'FaceTec_OCR_Customization.json'.
    FaceTecSDK.configureOCRLocalization(ocrLocalizationJSON);
  }

  public static fadeInMainUIContainer(): void {
    new SampleAppUIFunctions("#theme-transition-overlay").fadeOut(800);
    new SampleAppUIFunctions(".wrapping-box-container").fadeIn(800);
  }

  public static fadeInVocalIconContainer(): void {
    new SampleAppUIFunctions("#vocal-icon-container").fadeIn(800);
  }

  private static fadeInMainUIControls(callback?: () => void): void {
    if (SampleAppUtilities.isLikelyMobileDevice()) {
      new SampleAppUIFunctions("#custom-logo-container").fadeIn(800);
      new SampleAppUIFunctions("#vocal-icon-container").fadeIn(800);
    }

    new SampleAppUIFunctions("footer").fadeIn(800);
    new SampleAppUIFunctions("#controls").fadeIn(800, () => {
      SampleAppUtilities.enableControlButtons();
      SampleAppUtilities.enableVocalGuidanceButtons();

      if (typeof callback !== "undefined") {
        callback();
      }
    });
  }

  // Calculate passed in element height (including margins) to allow for dynamic sizing of parent containers
  private static calculateElementHeightWithMargins(element: HTMLElement): number {
    const elementComputedStyle: CSSStyleDeclaration = window.getComputedStyle(element);
    const elementOffsetHeight: number = element.offsetHeight;
    const elementMarginTop: number = parseFloat(elementComputedStyle.marginTop);
    const elementMarginBottom: number = parseFloat(elementComputedStyle.marginBottom);

    return elementOffsetHeight + elementMarginTop + elementMarginBottom;
  }

  public static fadeOutMainUIControlsAndFadeInOfficialIDInstructionsUI(): void {
    new SampleAppUIFunctions("#main-interface, #controls, #status, #custom-logo-container, #vocal-icon-container, #official-id-photo-session-cancel-container, footer").fadeOut(600, () => {
      (document.getElementById("official-id-photo-result-container") as HTMLElement).classList.add("display-none");
      (document.getElementById("official-id-photo-intro-container") as HTMLElement).classList.remove("display-none");
      (document.getElementById("official-id-photo-session-cancel-container") as HTMLElement).classList.remove("display-none");

      // Dynamically set the main interface container height based upon the height of the intro content
      var officialIDPhotoIntroContainerHeight: number = this.calculateElementHeightWithMargins((document.getElementById("official-id-photo-intro-container") as HTMLElement));
      (document.getElementById("main-interface") as HTMLElement).style.height = `${ officialIDPhotoIntroContainerHeight }px`;

      // Dynamically set the cancel button position to the top left corner within the main interface border if on desktop
      if (!this.isLikelyMobileDevice()) {
        (document.getElementById("official-id-photo-session-cancel-container") as HTMLElement).style.left = `${ ((window.innerWidth / 2)) - ((document.getElementById("main-interface") as HTMLElement).offsetWidth / 2) }px`;
      }

      this.enableAllButtons();
      new SampleAppUIFunctions("#main-interface, #official-id-photo-session-cancel-container, #official-id-photo-container").fadeIn(600);
    });

  }

  public static fadeOutOfficialIDPhotoUIAndFadeInMainUIControls(): void {
    this.disableAllButtons();
    new SampleAppUIFunctions(".wrapping-box-container, #official-id-photo-session-cancel-container, #official-id-photo-container").fadeOut(600, () => {
      // Remove height property on main interface that was dynamically set for Official ID Photo content and revert to value defined by CSS
      (document.getElementById("main-interface") as HTMLElement).style.removeProperty("height");

      (document.getElementById("official-id-photo-intro-container") as HTMLElement).classList.add("display-none");
      (document.getElementById("official-id-photo-result-container") as HTMLElement).classList.add("display-none");
      (document.getElementById("official-id-photo-session-cancel-container") as HTMLElement).classList.add("display-none");

      SampleAppUtilities.enableVocalGuidanceButtons();
      this.enableAllButtons();
      new SampleAppUIFunctions(".wrapping-box-container, #controls, #status, #custom-logo-container, #vocal-icon-container, footer").fadeIn(600);
    });
  }

  public static fadeInOfficialIDPhotoResultsUI(): void {
    (document.getElementById("official-id-photo-result-image") as HTMLElement).setAttribute("src", `data:image/jpeg;base64, ${SampleAppController.latestOfficialIDPhoto}`);
    (document.getElementById("official-id-photo-intro-container") as HTMLElement).classList.add("display-none");
    (document.getElementById("official-id-photo-result-container") as HTMLElement).classList.remove("display-none");
    (document.getElementById("official-id-photo-session-cancel-container") as HTMLElement).classList.remove("display-none");
    (document.querySelector(".wrapping-box-container") as HTMLElement).style.display = "block";

    // Dynamically set the main interface container height based upon the height of the result content
    var officialIDPhotoResultContainerHeight: number = this.calculateElementHeightWithMargins((document.getElementById("official-id-photo-result-container") as HTMLElement));
    (document.getElementById("main-interface") as HTMLElement).style.height = `${ officialIDPhotoResultContainerHeight }px`;

    // Dynamically set the cancel button position to the top left corner within the main interface border if on desktop
    if (!this.isLikelyMobileDevice()) {
      (document.getElementById("official-id-photo-session-cancel-container") as HTMLElement).style.left = `${ ((window.innerWidth / 2)) - ((document.getElementById("main-interface") as HTMLElement).offsetWidth / 2) }px`;
    }

    setTimeout(function() {
      new SampleAppUIFunctions(".wrapping-box-container, #official-id-photo-session-cancel-container, #official-id-photo-container").fadeIn(400);
    }, 200);
  }

  public static hideOfficialIDPhotoUIAndShowMainUIControlsDueToUnsuccessfulSession(): void {
    const mainInterface = document.getElementById("main-interface");
    if (mainInterface) {
      mainInterface.style.removeProperty("height");
    }
  }

  // Disable buttons to prevent hammering, fade out main interface elements, and reset the Session Review Screen data.
  public static fadeOutMainUIAndPrepareForSession(): void {
    SampleAppUtilities.disableControlButtons();

    if (SampleAppUtilities.isLikelyMobileDevice()) {
      new SampleAppUIFunctions("#custom-logo-container").fadeOut(800);
      new SampleAppUIFunctions("#vocal-icon-container").fadeOut(800);

      SampleAppUtilities.disableVocalGuidanceButtons();
    }

    new SampleAppUIFunctions("footer").fadeOut(800);
    new SampleAppUIFunctions("#controls").fadeOut(800);
    new SampleAppUIFunctions(".wrapping-box-container").fadeOut(800);
    new SampleAppUIFunctions("#official-id-photo-session-cancel-container").fadeOut(800);
    new SampleAppUIFunctions("#theme-transition-overlay").fadeIn(800);
  }

  private static disableControlButtons(): void {
    document.querySelectorAll("#controls > button").forEach(function(button: Element) {
      button.setAttribute("disabled", "true");
    });
  }

  public static enableControlButtons(): void {
    document.querySelectorAll("#controls > button").forEach(function(button: Element) {
      button.removeAttribute("disabled");
    });

    this.enableVocalGuidanceButtons();
  }

  public static showMainUI(): void {
    SampleAppUtilities.fadeInMainUIContainer();
    SampleAppUtilities.fadeInMainUIControls();
  }

  public static handleErrorGettingServerSessionToken(): void {
    SampleAppUtilities.showMainUI();
    DeveloperStatusMessages.logAndDisplayMessage("Session could not be started due to an unexpected issue during the network request.");
  }

  public static generateUUId(): string {
    // @ts-ignore
    return ("" + [1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c => { return (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16); });
  }

  public static formatUIForDevice(): void {
    window.addEventListener("keydown", SampleAppUtilities.onKeyDown);
    SampleAppUtilities.displayElementsAfterStyling();
  }

  public static keyboardAccessibilityStylingOn: boolean = false;

  public static onKeyDown(e: KeyboardEvent): void {
    if (e.key === "Tab") {
      SampleAppUtilities.enableKeyboardAccessibilityStyling(true);
    }
  }

  private static enableKeyboardAccessibilityStyling(enable: boolean): void {
    // Mobile not supported
    if (SampleAppUtilities.isLikelyMobileDevice() || SampleAppUtilities.keyboardAccessibilityStylingOn) { return; }

    SampleAppUtilities.keyboardAccessibilityStylingOn = true;

    const buttons: HTMLCollectionOf<Element> = document.getElementsByClassName("ft-button");

    for (var i: number = 0; i < buttons.length; i++) {
      const element: HTMLElement = buttons[i] as HTMLElement;

      if (enable) {
        element.style.outline = "revert";
      }
      else {
        element.style.outline = "none";
      }
    }
  }

  private static displayElementsAfterStyling(): void {
    document.querySelectorAll("button").forEach(function(element: HTMLButtonElement) {
      element.classList.add("button-transitions");
    });
    new SampleAppUIFunctions("body").fadeIn(800);
  }

  private static disableVocalGuidanceButtons(): void {
    document.querySelectorAll(".vocal-icon").forEach((button: Element) => {
      (<HTMLButtonElement>button).setAttribute("disabled", "true");
    });
  }

  private static enableVocalGuidanceButtons(): void {
    document.querySelectorAll(".vocal-icon").forEach((button: Element) => {
      (<HTMLButtonElement>button).removeAttribute("disabled");
    });
  }

  public static isLikelyMobileDevice(): boolean {
    var isMobileDeviceUA: boolean = !!(/Android|iPhone|iPad|iPod|IEMobile|Mobile|mobile/i.test(navigator.userAgent || ""));

    // ChromeOS/Chromebook detection.
    if (isMobileDeviceUA && ((navigator.userAgent.indexOf("CrOS") !== -1) || (navigator.userAgent.indexOf("Chromebook") !== -1))) {
      isMobileDeviceUA = false;
    }

    // Mobile device determination based on portrait / landscape and user agent.
    if (screen.width < screen.height || isMobileDeviceUA) {
      // Assume mobile device when in portrait mode or when determined by the user agent.
      return true;
    }
    else {
      return false;
    }
  }

  public static disableAllButtons(): void {
    document.querySelectorAll("button").forEach((btn) => btn.setAttribute("disabled", "true"));
  }

  public static enableAllButtons(): void {
    document.querySelectorAll("button").forEach((btn) => btn.removeAttribute("disabled"));
  }

  public static fadeInBlurOverlay(): void {
    (document.getElementById("controls") as HTMLElement).classList.add("blur-content");
  }

  public static fadeOutBlurOverlay(): void {
    if ((document.getElementById("controls") as HTMLElement).classList.contains("blur-content")) {
      (document.getElementById("controls") as HTMLElement).classList.remove("blur-content");
    }
  }
}
